import { createBrowserHistory } from 'history';
import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Router, Switch, matchPath, useLocation } from 'react-router-dom';

import { ContainerContext } from '../common/container/ContainerContext';
import NotFoundErrorPage from '../common/errorPage/Page404/NotFoundErrorPage';
import PageServiceNotAvailable from '../common/errorPage/PageServiceNotAvailable/PageServiceNotAvailable';
import { UserSystemRoleModelPermissionMap } from '../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { AnalyticsMainPage } from '../view/admin/page/analytics/main/AnalyticsMainPage';
import { AnalyticsProjectPage } from '../view/admin/page/analytics/projects/AnalyticsProjectPage';
import { AnalyticsUserPage } from '../view/admin/page/analytics/user/AnalyticsUserPage';
import { SettingsPageTree } from '../view/admin/page/settings/SettingsPageTree';
import EmptyLayoutRoute from '../view/layout/empty/EmptyLayoutRoute';
import { MainLayout } from '../view/layout/main/MainLayout';
import { ForcePasswordChangePage } from '../view/layout/main/parts/LeftNavigation/parts/ForcePasswordChangePage';
import { IMainLayoutDomainStore } from '../view/layout/main/store/domain/IMainLayoutDomainStore';
import { withLayoutDomain } from '../view/layout/main/store/domain/withLayoutDomain';
import { LearningPageShell } from '../view/shared/page/learning/LearningShell';
import { ApplicationEdit } from '../view/user/page/application/info/ApplicationEdit';
import { ApplicationInfo } from '../view/user/page/application/info/ApplicationInfo';
import { LogoutPage } from '../view/user/page/login/LogoutPage';
import { SignInPage } from '../view/user/page/login/SignInPage';
import { SignInPageWithLogout } from '../view/user/page/login/SignInPageWithLogout';
import { SSOOAUTHRedirectPage } from '../view/user/page/login/sso/SSOOAUTHRedirectPage';
import { NotificationPage } from '../view/user/page/notification/NotificationPage';
import { ProjectForm } from '../view/user/page/project/form/ProjectForm';
import { ProjectInfo } from '../view/user/page/project/info/ProjectInfo';
import { ProjectInvitationJoinPage } from '../view/user/page/project/invitationJoin/ProjectInvitationJoinPage';
import { ProjectListPage } from '../view/user/page/project/list/ProjectListPage';
import { ApplicationAdditionalRoutes, IApplicationAdditionalRoutes } from './ApplicationAdditionalRoutes';
import { ApplicationRouterExtensions, IApplicationRouterExtension } from './ApplicationRouterExtensions';
import { ApplicationRoutes } from './constants/routes';
import { RedirectGuard } from './guards/RedirectGuard';
import { useI18n } from './language/useI18n';
import AccessDeniedPage from '../common/errorPage/Page403/AccessDenied';


const SettingsPage = React.lazy(() =>
  import('../view/admin/page/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

export const appHistory: any = createBrowserHistory();

type SettingsRoute = {
  key: string;
  path: string;
  pageTitle: string;
  component: any;
  allowedPermissions: string[];
};

const renderWithLayoutDomain = (Component: any) => {
  const Wrapped = withLayoutDomain(Component as any);
  return (props: any) => <Wrapped {...props} />;
};

const renderWithAnalyticsAccessGuard = (Component: any) => {
  const Wrapped = withLayoutDomain((props: any) => {
    const editProjectPermission = props.layoutDomain.userHaveAnyAccessToEditProject();
    if (!editProjectPermission.isCanEditFields && !editProjectPermission.isCanEditTeam) {
      return <Redirect to={ApplicationRoutes[403]} push={false} />;
    }

    return (
      <AnalyticsPageAccessGuard layoutDomain={props.layoutDomain}>
        <Component {...props} />
      </AnalyticsPageAccessGuard>
    );
  });

  return (props: any) => <Wrapped {...props} />;
};


const renderWithProjectsAccessGuard = (Component: any) => {
  const Wrapped = withLayoutDomain((props: any) => {
    const isAllowed = props.layoutDomain.userHaveAnyAccess([
      UserSystemRoleModelPermissionMap['global-allow-any'],
    ]);

    if (!isAllowed) {
      return <Redirect to={ApplicationRoutes[403]} push={false} />
    }

    return <Component {...props} />;
  });

  return (props: any) => <Wrapped {...props} />;
}

const AnalyticsPageAccessGuard = ({
  layoutDomain,
  children,
}: {
  layoutDomain: IMainLayoutDomainStore;
  children: React.ReactNode;
}) => {
  const isAllowed = layoutDomain.userHaveAnyAccess([UserSystemRoleModelPermissionMap['global-allow-any']]);

  if (!isAllowed) {
    return <Redirect to={ApplicationRoutes[403]} push={false} />;
  }

  return <>{children}</>;
};

const routeTitleMap: { path: string | string[]; titleKey: string; exact?: boolean }[] = [
  { path: ApplicationRoutes.ProjectAll, titleKey: 'pageTitles.projects' },
  { path: ApplicationRoutes.ProjectInfo, titleKey: 'pageTitles.project' },
  { path: ApplicationRoutes.ProjectCreate, titleKey: 'pageTitles.addProject' },
  {
    path: [ApplicationRoutes.ProjectEdit, ApplicationRoutes.ProjectEditApplication],
    titleKey: 'pageTitles.editProject',
  },
  { path: ApplicationRoutes.ApplicationInfo, titleKey: 'pageTitles.systemPage' },
  { path: ApplicationRoutes.Settings, titleKey: 'pageTitles.settings' },
  { path: ApplicationRoutes.Notification, titleKey: 'pageTitles.notifications' },
  { path: ApplicationRoutes.Analytics, titleKey: 'pageTitles.analytics' },
  { path: ApplicationRoutes.LearningUnitCreate, titleKey: 'pageTitles.createUnit' },
  { path: ApplicationRoutes.LearningUnitEdit, titleKey: 'pageTitles.editUnit' },
  { path: ApplicationRoutes.LearningUnitPassing, titleKey: 'pageTitles.passing' },
  { path: ApplicationRoutes.LearningUnitPreview, titleKey: 'pageTitles.unitPreview' },
  { path: ApplicationRoutes.LearningUserRating, titleKey: 'pageTitles.userRating' },
  { path: ApplicationRoutes.LearningRoot, titleKey: 'pageTitles.learning' },
];

const MainAppShell = ({
  settingsPages,
  routerExtension,
  additionalRoutes,
}: {
  settingsPages: SettingsRoute[];
  routerExtension: IApplicationRouterExtension[];
  additionalRoutes: IApplicationAdditionalRoutes[];
}) => {
  const location = useLocation();
  const { translateAnyway } = useI18n();
  const wrappedSettingsRoutes = useMemo(() => {
    return settingsPages.map((s) => ({
      ...s,
      render: renderWithLayoutDomain(s.component),
    }));
  }, [settingsPages]);

  const activeAllowedPermissions = useMemo((): UserSystemRoleModelPermissionMap[] => {
    for (const s of settingsPages) {
      if (matchPath(location.pathname, { path: s.path, exact: true })) {
        return s.allowedPermissions as UserSystemRoleModelPermissionMap[];
      }
    }
    for (const ext of routerExtension) {
      if (matchPath(location.pathname, { path: ext.path as string, exact: true })) {
        return ext.allowedPermissions;
      }
    }
    return [];
  }, [location.pathname, settingsPages, routerExtension]);

  const activeLayoutOptions = useMemo(() => {
    const extensionRoute = routerExtension.find((route) =>
      matchPath(location.pathname, {
        path: route.path as string,
        exact: true,
      }),
    );

    if (!extensionRoute) {
      return {};
    }

    return {
      showNavBar: extensionRoute.showNavBar,
      showPaddings: extensionRoute.showPaddings,
      overflowY: extensionRoute.overflowY,
    };
  }, [routerExtension, location.pathname]);

  const pageTitle = useMemo(() => {
    for (const route of routeTitleMap) {
      const paths = Array.isArray(route.path) ? route.path : [route.path];
      for (const p of paths) {
        if (matchPath(location.pathname, { path: p, exact: route.exact !== false })) {
          return translateAnyway(route.titleKey);
        }
      }
    }

    const settingsRoute = settingsPages.find((s) => matchPath(location.pathname, { path: s.path, exact: true }));
    if (settingsRoute?.pageTitle) {
      return translateAnyway(settingsRoute.pageTitle);
    }

    const extensionRoute = routerExtension.find((route) =>
      matchPath(location.pathname, { path: route.path as string, exact: true }),
    );
    if (extensionRoute?.pageTitle) {
      return translateAnyway(extensionRoute.pageTitle);
    }

    return '';
  }, [location.pathname, translateAnyway, settingsPages, routerExtension]);

  return (
    <MainLayout pageTitle={pageTitle} allowedPermissions={activeAllowedPermissions} {...activeLayoutOptions}>
      <Suspense fallback={<div />}>
        <Switch>
          <Route
            exact
            path={ApplicationRoutes.Root}
            render={() => <Redirect to={ApplicationRoutes.LearningRoot} push={false} />}
          />

          <Route exact path={ApplicationRoutes.ProjectAll} render={renderWithLayoutDomain(ProjectListPage)} />
          <Route exact path={ApplicationRoutes.ProjectInfo} render={renderWithLayoutDomain(ProjectInfo)} />
          <Route exact path={ApplicationRoutes.ProjectCreate} render={renderWithProjectsAccessGuard(ProjectForm)} />
          <Route exact path={ApplicationRoutes.ProjectEditApplication} render={renderWithLayoutDomain(ProjectForm)} />
          <Route exact path={ApplicationRoutes.ProjectEdit} render={renderWithLayoutDomain(ProjectForm)} />
          <Route exact path={ApplicationRoutes.SystemCreate} render={renderWithLayoutDomain(ApplicationEdit)} />

          <Route
            exact
            path={ApplicationRoutes.ProjectInvitationJoin}
            render={renderWithLayoutDomain(ProjectInvitationJoinPage)}
          />

          <Route exact path={ApplicationRoutes.ApplicationInfo} render={renderWithLayoutDomain(ApplicationInfo)} />

          {/* Settings root  */}
          <Route exact path={ApplicationRoutes.Settings} render={renderWithProjectsAccessGuard(SettingsPage)} />

          {/* Learning  */}
          <Route path={ApplicationRoutes.LearningRoot} render={renderWithLayoutDomain(LearningPageShell)} />

          {/* Analytics */}
          <Route exact path={ApplicationRoutes.Analytics} render={renderWithAnalyticsAccessGuard(AnalyticsMainPage)} />
          <Route
            exact
            path={ApplicationRoutes.AnalyticsProject}
            render={renderWithAnalyticsAccessGuard(AnalyticsProjectPage)}
          />
          <Route
            exact
            path={ApplicationRoutes.AnalyticsUser}
            render={renderWithAnalyticsAccessGuard(AnalyticsUserPage)}
          />
          <Route
            exact
            path="/analytics/learning"
            render={() => <Redirect to={ApplicationRoutes.Analytics} push={false} />}
          />

          {/* Notifications */}
          <Route exact path="/notification" render={renderWithLayoutDomain(NotificationPage)} />

          {/* dynamic settings routes */}
          {wrappedSettingsRoutes.map((s) => (
            <Route key={s.path} exact path={s.path} render={s.render} />
          ))}

          {/* extension routes */}
          {routerExtension.map((routeSettings) => (
            <Route
              key={routeSettings.path as string}
              exact
              path={routeSettings.path as string}
              render={(props) => React.createElement(routeSettings.component, { ...props })}
            />
          ))}

          {additionalRoutes.map(({ Component, key }) => (
            <React.Fragment key={key}>{Component}</React.Fragment>
          ))}

          <Redirect to="/404" />
        </Switch>
      </Suspense>
    </MainLayout>
  );
};

const ApplicationRouter = () => {
  const container = useContext(ContainerContext);
  const routerExtension = container.get<IApplicationRouterExtension[]>(ApplicationRouterExtensions);
  const [settingsPages, setSettingsPages] = useState<SettingsRoute[]>([]);

  const settingsPageTree = container.get<typeof SettingsPageTree>(SettingsPageTree);
  const additionalRoutes = container.get<IApplicationAdditionalRoutes[]>(ApplicationAdditionalRoutes);

  useEffect(() => {
    const routers = Object.values(settingsPageTree).reduce((result, item) => {
      result.push(...item.routers);
      return result;
    }, [] as SettingsRoute[]);

    setSettingsPages(routers);
  }, [settingsPageTree]);

  return (
    <Router history={appHistory}>
      <RedirectGuard>
        {settingsPages.length > 0 && (
          <Switch>
            {/* Empty layout */}
            <EmptyLayoutRoute
              exact
              path={ApplicationRoutes.ForcePasswordChange}
              component={ForcePasswordChangePage}
              key="force-password-change"
            />
            <EmptyLayoutRoute exact path={ApplicationRoutes.Login} component={SignInPage} key="login" />
            <EmptyLayoutRoute
              exact
              path={ApplicationRoutes.SSORedirect}
              component={SSOOAUTHRedirectPage}
              key="sso-redirect"
            />
            <EmptyLayoutRoute
              exact
              path={ApplicationRoutes.LoginTimeout}
              component={SignInPageWithLogout}
              key="login-timeout"
            />
            <EmptyLayoutRoute exact path={ApplicationRoutes.Logout} component={LogoutPage} key="logout" />
            <EmptyLayoutRoute exact path="/404" component={NotFoundErrorPage} key="not-found" />
            <EmptyLayoutRoute
              exact
              path="/serviceNotAvailable"
              component={PageServiceNotAvailable}
              key="service-unavailable"
            />
            <EmptyLayoutRoute exact path={ApplicationRoutes[403]} component={AccessDeniedPage} key='403'/>

            {/* Main layout for everything else */}
            <Route
              path="/"
              render={() => (
                <MainAppShell
                  settingsPages={settingsPages}
                  routerExtension={routerExtension}
                  additionalRoutes={additionalRoutes}
                />
              )}
            />
          </Switch>
        )}
      </RedirectGuard>
    </Router>
  );
};

export default ApplicationRouter;
